import React, { useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from 'context/auth';
import { Box, Button, FormControl, FormLabel, Heading, Input, NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper, Textarea, Text, Divider, useToast, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Tag, Center, Spinner, InputGroup, InputRightElement, Card, CardBody, Flex, Spacer, CloseButton, Checkbox } from '@chakra-ui/react';
import { config, mfrProcessSelectables, subSystemSelectables, materialSelectables, finishSelectables, prioritySelectables } from 'util/constants';
import { CreatableSelect, Select } from "chakra-react-select";
import { MCFFormsContext } from 'context/mcfforms';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';

export default function RejectReviewForm(props) {
    const { user } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const toast = useToast();
    const { MCFForms, getMCFForms } = useContext(MCFFormsContext);
    const { objectId: objectIdParam } = useParams();
    const [authorizedUser, setAuthorizedUser] = useState(false);
    const [form, setForm] = useState(null);
    const [reasons, setReasons] = useState([]);
    const timeLoaded = useRef(null);

    const navigate = useNavigate();

    const errorToast = () => {
        toast({
            title: 'Error',
            description: 'There was an error processing your request',
            status: 'error',
            duration: 5000,
            isClosable: true,
        });
    }

    const handleSubmit = (e) => {
        if (reasons.length > 0) {
            setLoading(true);
            let formData = {};
            
            fetch(`/transactions/getMCFForm/${objectIdParam}`, {
                method: 'GET',
            }).then(response => {
                if (response.status !== 200) {
                    throw `${response.status}: ${response.statusText}`
                }
                return response.json();
            }
            ).then(data => {
                if (new Date(timeLoaded.current).getTime() > new Date(data.docs.updatedAt).getTime())
                {
                    let currentReasons = JSON.stringify(reasons);
                    let previousReasons = JSON.stringify(data.docs.rejection.recommendations);
                    formData.rejectReasons = reasons;
                    if (currentReasons !== previousReasons)
                    {
                        formData.notes = data.docs.notes;
                        formData.notes.push({ 
                            timestamp: Date.now(),
                            note: `Recommendations reviewed by ${user.firstName} ${user.lastName.charAt(0)}.`
                        });
                        fetch(`/transactions/reviewRejectedMCFForm/${objectIdParam}`, {
                            method: 'POST',
                            // We convert the React state to JSON and send it as the POST body
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(formData)
                        }).then(response => {
                            if (response.status !== 200) {
                                throw `${response.status}: ${response.statusText}`
                            }
                            return response.text();
                        // @ts-ignore
                        }).then(function(data) {
                            // Success
                            getMCFForms(() => {
                                setTimeout(() => {
                                    toast({
                                        title: 'Part MCF Updated',
                                        status: 'success',
                                        duration: 5000,
                                        isClosable: true,
                                    });
                                    setLoading(false);
                                    navigate('/');
                                }, 500);
                            });
                        })
                        .catch(err => {
                            setLoading(false);
                            console.log(err);
                            errorToast();
                        });
                    } else {
                        // Nothing changed
                        getMCFForms(() => {
                            setTimeout(() => {
                                toast({
                                    title: 'Rejected Part MCF Updated',
                                    status: 'success',
                                    duration: 5000,
                                    isClosable: true,
                                });
                                setLoading(false);
                                navigate('/');
                            }, 500);
                        });
                    }
                } else {
                    toast({
                        title: 'Error',
                        description: 'Current document is outdated. Please refresh.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                    setLoading(false);
                }
            }).catch(err => {
                setLoading(false);
                errorToast();
                console.error(err);
            });
        }
        else {
            toast({
                title: 'Error',
                description: 'Enter a reason for rejecting or recommendation',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    }
    
    const handleCheckbox = (value, i) => {
        value.fixed =! value.fixed;
        // 1. Make a shallow copy of the items
        let items = [...reasons];
        // 2. Make a shallow copy of the item you want to mutate
        let item = {...reasons[i]};
        // 3. Replace the property you're intested in
        item.fixed = value.fixed;
        // 4. Put it back into our array. N.B. we *are* mutating the array here, 
        //    but that's why we made a copy first
        items[i] = item;
        // 5. Set the state to our new copy
        setReasons(items);
    }

    useEffect(() => {
        try
        {
            fetch(`/transactions/getMCFForm/${objectIdParam}`, {
                method: 'GET',
            }).then(response => {
                if (response.status !== 200) {
                    throw `${response.status}: ${response.statusText}`
                }
                return response.json();
            }
            ).then(data => {
                setReasons(data.docs.rejection.recommendations);
                setTimeout(() => setForm(data.docs), 250);
                if (!data.docs.approved && !data.docs.manufactured && (data.docs.googleId === user.googleId || user.admin))
                {
                    setAuthorizedUser(true);
                }
                timeLoaded.current = data.timestamp;
            }).catch(err => {
                console.error(err);
            });

        } catch (e)
        {
            console.log(e);
        }
    }, []);

    if (!form)
    {
        return (<>
        <Box margin={'0 auto'} mt={"1em"} width={{ base: '85%', md: '66%', lg: '50%' }}>
            <Tag backgroundColor={'white'}>
                <Breadcrumb separator={<ChevronRightIcon color='gray.500' />}>
                    <BreadcrumbItem>
                        <BreadcrumbLink as={RouterLink} to={`/`}>Home</BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbItem>
                        <BreadcrumbLink>Edit Form</BreadcrumbLink>
                    </BreadcrumbItem>
                </Breadcrumb>
            </Tag>
            <Center><Spinner mt={'2em'} size='lg' /></Center>
        </Box></>)
    }

    return (
        <>
            <Box margin={'0 auto'} mt={"1em"} width={{ base: '85%', md: '66%', lg: '50%' }}>
                <Tag backgroundColor={'white'}>
                    <Breadcrumb separator={<ChevronRightIcon color='gray.500' />}>
                        <BreadcrumbItem>
                            <BreadcrumbLink as={RouterLink} to={`/`}>Home</BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbItem>
                            <BreadcrumbLink>Review Rejected Part</BreadcrumbLink>
                        </BreadcrumbItem>
                    </Breadcrumb>
                </Tag>
            </Box>
            <Box margin={'1em auto'} width={{ base: '85%', md: '66%', lg: '50%' }}
                p={4}
                borderWidth="1px"
                rounded="lg"
                shadow="1px 1px 3px rgba(0,0,0,0.3)"
                bgColor={'#fff'}>
                {authorizedUser ? (<>
                <Heading mb={2} as="h1" size="lg" textAlign="center">
                    Review Rejected Part MCF
                </Heading>
                <Divider />
                <form autoComplete="off">
                    <FormControl isRequired mt={2}>
                        <FormLabel>File Path</FormLabel>
                        <Input isReadOnly onFocus={e => e.target.blur() } variant='filled' name="filePath" type="text" size="sm" defaultValue={form.filePath}/>
                    </FormControl>
                    <FormControl isRequired mt={2}>
                        <FormLabel>File Name</FormLabel>
                        <Input isReadOnly onFocus={e => e.target.blur() } variant='filled' name="fileName" type="text" size="sm" defaultValue={form.fileName}/>
                    </FormControl>
                    <FormControl mt={2}>
                        <FormLabel>Reason/Recommendations</FormLabel>
                    </FormControl>
                    {reasons.map((e, i) => {
                            return (
                            <Card mt={2} size="sm" key={i}>
                                <CardBody>
                                    <Flex>
                                        <Checkbox isChecked={e.fixed} onChange={checkbox => handleCheckbox(e, i) } colorScheme='green'>
                                            <Text>{e.recommendation}</Text>
                                        </Checkbox>
                                        <Spacer/>
                                    </Flex>
                                </CardBody>
                            </Card>)
                    })}
                    <Button
                        isLoading={loading}
                        name="submitButton"
                        loadingText='Updating'
                        colorScheme="teal"
                        width="full"
                        mt="4"
                        onClick={handleSubmit}
                    >
                        Update
                    </Button>
                </form>
                </>
                ) : (<><Center>Not Authorizied</Center></>)}
            </Box>
        </>
    );
}
